package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.FontStyle
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.TextTypography
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.render.model.GradientStop
import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.MaskType
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.style.ShadowSpec
import top.wcpe.allincore.ui.runtime.style.StyleVisual
import kotlin.math.PI

/**
 * 视觉属性贡献者：text / color / size / typography / font* / background / tooltip / cornerRadius /
 * border* / gradient* / radial* / shadow* / mask* 等。
 *
 * color 走 ARGB 通道 lerp（由 `IrInterpreter#canInterpolate` 识别），其他数值属性按线性插值。
 */
@Suppress("TooManyFunctions", "MagicNumber")
internal object VisualPropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.VisualPropContributor")

    @Suppress("LongMethod", "CyclomaticComplexMethod")
    fun contribute(m: MutableMap<String, PropBinding>) {
        // ---------- 文本：text / color / size（Text / Button / Dropdown 多类型分发） ----------
        m["text"] = PropBinding.of(
            { el, v ->
                val s = v?.toString() ?: ""
                when (el) {
                    is Text -> el.text(s)
                    is Button -> el.text(s)
                    else -> PropDiag.unsupported(LOGGER, el, "text")
                }
            },
            { el -> textValueOf(el) },
        )
        m["color"] = PropBinding.of(
            { el, v ->
                if (v != null) {
                    when (el) {
                        is Text -> el.color(ValueCoercion.toIntColor(v))
                        is Button -> el.color(ValueCoercion.toIntColor(v))
                        else -> PropDiag.unsupported(LOGGER, el, "color")
                    }
                }
            },
            { el ->
                when (el) {
                    is Text -> el.color()
                    is Button -> el.color()
                    else -> null
                }
            },
        )
        m.textProp(
            "textOverrides.color",
            { t, v -> t.textOverrideColor(v?.let { ValueCoercion.toIntColor(it) }) },
            // 动画起点读原始 override 字段（未覆盖为 null），而非 [Text.color] 最终解析色：
            // 解析色会混入 textStyle/color 声明层，使「元素声明层是否有覆盖」语义失真。
            { t -> t.textOverrideColor() },
        )
        m["size"] = PropBinding.of(
            { el, v ->
                if (v != null) {
                    val f = ValueCoercion.toFloat(v)
                    when (el) {
                        is Text -> el.size(f)
                        is Button -> el.textScale(f)
                        is Dropdown -> el.size(f)
                        else -> PropDiag.unsupported(LOGGER, el, "size")
                    }
                }
            },
            { el ->
                when (el) {
                    is Text -> el.size().toDouble()
                    is Button -> el.textScale().toDouble()
                    is Dropdown -> el.size().toDouble()
                    else -> null
                }
            },
        )
        // textOverrides.size 与旧 size 同语义（MC 字体缩放倍率）。
        m["textOverrides.size"] = m.getValue("size")

        // typography：Text 专属语义化排版角色，非法值回退 bodyMedium。
        m.textProp(
            "typography",
            { t, v ->
                if (v == null) {
                    t.typography(null)
                } else {
                    val typo = TextTypography.tryParse(v.toString()) ?: run {
                        LOGGER.warn("非法 typography: $v，已回退为 bodyMedium")
                        TextTypography.BODY_MEDIUM
                    }
                    t.typography(typo)
                }
            },
            { t -> t.typography()?.dslName },
        )
        // textStyle 是 v2 DSL 的标准入口，与 runtime 的 typography 语义保持一致。
        m["textStyle"] = m.getValue("typography")

        // textOverflow / maxLines：Text 与 Button 共用的多行截断属性（其它元素打 unsupported）。
        m["textOverflow"] = PropBinding.writeOnly { el, v ->
            when (el) {
                // Text 的 null 重置为默认 wrap（与默认换行一致），而非清成 null
                is Text -> el.textOverflow(v?.toString() ?: "wrap")
                is Button -> el.textOverflow(v?.toString())
                else -> PropDiag.unsupported(LOGGER, el, "textOverflow")
            }
        }
        m["maxLines"] = PropBinding.writeOnly { el, v ->
            val iv = if (v == null) null else (v as Number).toInt()
            when (el) {
                is Text -> el.maxLines(iv)
                is Button -> el.maxLines(iv)
                else -> PropDiag.unsupported(LOGGER, el, "maxLines")
            }
        }

        // ---------- Text 专属（decipher / richText / 排版增强），均走通用 prop 包、null 透传回退默认 ----------
        m.textProp(
            "decipher",
            { t, v -> if (v != null) t.decipher(ValueCoercion.toBool(v)) },
            { t -> t.decipher() },
        )
        m.textProp(
            "richText",
            { t, v -> if (v != null) t.richText(ValueCoercion.toBool(v)) },
            { t -> t.richText() },
        )
        m.textProp(
            "decipherDurationMs",
            { t, v -> if (v != null) t.decipherDurationMs(ValueCoercion.toFloat(v)) },
            { t -> t.decipherDurationMs().toDouble() },
        )
        m.textProp(
            "decipherCharset",
            { t, v -> t.decipherCharset(v?.toString()) },
            { t -> t.decipherCharset() },
        )
        m.textProp(
            "letterSpacing",
            { t, v -> t.letterSpacing(v?.let { ValueCoercion.toFloat(it) }) },
            { t -> t.letterSpacing()?.toDouble() },
        )
        m.textProp(
            "lineHeight",
            { t, v -> t.lineHeight(v?.let { ValueCoercion.toFloat(it) }) },
            { t -> t.lineHeight()?.toDouble() },
        )
        m.textProp(
            "textDecoration",
            { t, v -> t.textDecoration(v?.toString()) },
            { t -> t.textDecoration() },
        )

        // ---------- 字体样式：font 系列挂 UIElement 基类，任意带文字控件统一适用（shadow/bold/italic 三态） ----------
        m["font"] = PropBinding.of(
            { el, v -> if (v == null) el.font(null) else mergeFont(el) { name(v.toString()) } },
            { el -> el.font()?.name() },
        )
        m["fontBold"] = PropBinding.writeOnly { el, v -> mergeFont(el) { bold(v?.let { ValueCoercion.toBool(it) }) } }
        m["fontItalic"] =
            PropBinding.writeOnly { el, v -> mergeFont(el) { italic(v?.let { ValueCoercion.toBool(it) }) } }
        m["fontShadow"] =
            PropBinding.writeOnly { el, v -> mergeFont(el) { shadow(v?.let { ValueCoercion.toBool(it) }) } }

        // ---------- 背景 / tooltip / hudAnchor / cursor ----------
        m["background"] = PropBinding.of(
            { el, v -> if (v != null) el.style().backgroundColor(ValueCoercion.toIntColor(v)) },
            { el -> el.style().backgroundColor() },
        )
        m["tooltip"] = PropBinding.of(
            { el, v -> el.tooltip(v?.toString() ?: "") },
            { el -> el.tooltip() },
        )
        // hudAnchor：HUD root 锚点声明（平台层据屏幕/元素尺寸自动贴边/居中），非 HUD 场景纯元数据。
        m["hudAnchor"] = PropBinding.writeOnly { el, v -> el.hudAnchor(v?.toString()) }
        // screenBackground：仅 Screen 根元素消费；null 恢复默认开启。
        m["screenBackground"] = PropBinding.of(
            { el, v -> el.screenBackground(v?.let { ValueCoercion.toBool(it) } ?: true) },
            { el -> el.screenBackground() },
        )
        // cursor：OS 光标形状（如 "pointer"），由 InputDispatcher 在 hover 命中末端解析上报；空/null 归一为默认箭头。
        m["cursor"] = PropBinding.writeOnly { el, v -> el.cursor(v?.toString()) }

        // ---------- 圆角：cornerRadius（单值/CSS 简写数组）+ 单角 cornerRadiusTL/TR/BR/BL ----------
        m["cornerRadius"] = PropBinding.of(
            { el, v ->
                when (v) {
                    null -> Unit
                    is List<*> -> toNumberList(v)?.let { applyCornerRadii(el, CornerRadii.parseCss(it)) }
                    else -> applyCornerRadii(el, CornerRadii.uniform(ValueCoercion.toFloat(v)))
                }
            },
            { el -> el.style().visual().cornerRadiusTL().toDouble() },
        )
        m["cornerRadiusTL"] = cornerCornerBinding(CornerSide.TL) { it.cornerRadiusTL() }
        m["cornerRadiusTR"] = cornerCornerBinding(CornerSide.TR) { it.cornerRadiusTR() }
        m["cornerRadiusBR"] = cornerCornerBinding(CornerSide.BR) { it.cornerRadiusBR() }
        m["cornerRadiusBL"] = cornerCornerBinding(CornerSide.BL) { it.cornerRadiusBL() }

        // ---------- 边框 ----------
        m.visualFloat("borderWidth", { vis, f -> vis.borderWidth(f) }, { it.borderWidth() })
        m.visualColor("borderColor", { vis, c -> vis.borderColor(c) }, { it.borderColor() })

        // ---------- 渐变 ----------
        m["gradientType"] = PropBinding.of(
            { el, v -> if (v != null) setGradientType(el, v) },
            { el -> el.style().visual().gradientType().name.lowercase() },
        )
        m["gradientAngle"] = PropBinding.of(
            { el, v ->
                if (v != null) {
                    // DSL 角度为 CSS 度数（0°=下→上, 90°=左→右, 180°=上→下），shader 期望弧度，须 deg-90 偏移 + toRadians。
                    val deg = ValueCoercion.toFloat(v)
                    el.style().visual().gradientAngleRadians(((deg.toDouble() - 90.0) * PI / 180.0).toFloat())
                }
            },
            { el -> el.style().visual().gradientAngleRadians().toDouble() * 180.0 / PI + 90.0 },
        )
        // gradientStops = [[offset(0..1), argbColor], ...]
        m["gradientStops"] = PropBinding.of(
            { el, v ->
                when {
                    v == null -> Unit
                    v !is List<*> -> LOGGER.warn("gradientStops 期望 List，实际 ${v::class.simpleName}")
                    else -> el.style().visual().gradientStops(parseGradientStops(v))
                }
            },
            { el ->
                // 把 List<GradientStop> 转回 List<List<Object>>，供动画 transition 起点读取
                val stops = el.style().visual().gradientStops()
                if (stops.isEmpty()) null else stops.map { listOf(it.offset.toDouble(), it.argb) }
            },
        )
        // 简化两色渐变：折叠成首尾两个 GradientStop（Button 仍由 ButtonStatePropContributor 覆盖走专用字段）。
        m["gradientStartColor"] = PropBinding.writeOnly { el, v ->
            if (v != null) applyGradientEdgeColor(el, ValueCoercion.toIntColor(v), atStart = true)
        }
        m["gradientEndColor"] = PropBinding.writeOnly { el, v ->
            if (v != null) applyGradientEdgeColor(el, ValueCoercion.toIntColor(v), atStart = false)
        }

        // ---------- 径向渐变中心 / 半径（仅 gradientType=radial 生效） ----------
        m.visualFloat("radialCenterX", { vis, f -> vis.radialCenterX(f) }, { it.radialCenterX() })
        m.visualFloat("radialCenterY", { vis, f -> vis.radialCenterY(f) }, { it.radialCenterY() })
        m.visualFloat("radialRadius", { vis, f -> vis.radialRadius(f) }, { it.radialRadius() })

        // ---------- 单阴影 + 多重阴影 ----------
        m.visualFloat("shadowOffsetX", { vis, f -> vis.shadowOffsetX(f) }, { it.shadowOffsetX() })
        m.visualFloat("shadowOffsetY", { vis, f -> vis.shadowOffsetY(f) }, { it.shadowOffsetY() })
        m.visualFloat("shadowBlur", { vis, f -> vis.shadowBlur(f) }, { it.shadowBlur() })
        m.visualColor("shadowColor", { vis, c -> vis.shadowColor(c) }, { it.shadowColor() })
        m.visualFloat("shadowSpread", { vis, f -> vis.shadowSpread(f) }, { it.shadowSpread() })
        m.visualBool("shadowInset", { vis, b -> vis.shadowInset(b) }, { it.shadowInset() })
        // 多重阴影：list-of-map，非空时 BoxRenderer 优先于单阴影字段；阴影规格不参与动画故 writeOnly。
        m["boxShadows"] = PropBinding.writeOnly { el, v ->
            when {
                v == null -> el.style().visual().boxShadows(null)
                v !is List<*> -> LOGGER.warn("boxShadows 期望 List，实际 ${v::class.simpleName}")
                else -> el.style().visual().boxShadows(parseBoxShadows(v))
            }
        }

        // ---------- 遮罩：mask / maskParam1..3 + PIE 语义化别名 ----------
        m["mask"] = PropBinding.of(
            { el, v -> if (v != null) setMaskType(el, v) },
            { el -> el.style().visual().maskType().name.lowercase() },
        )
        m.visualFloat("maskParam1", { vis, f -> vis.maskParam1(f) }, { it.maskParam1() })
        m.visualFloat("maskParam2", { vis, f -> vis.maskParam2(f) }, { it.maskParam2() })
        m.visualFloat("maskParam3", { vis, f -> vis.maskParam3(f) }, { it.maskParam3() })
        // PIE 别名：写入强制 maskType=PIE 并写对应 maskParam（startAngle=param1, sweepAngle=param2）。
        m["mask.pie.startAngle"] = PropBinding.of(
            { el, v -> if (v != null) el.style().visual().maskType(MaskType.PIE).maskParam1(ValueCoercion.toFloat(v)) },
            { el -> el.style().visual().maskParam1().toDouble() },
        )
        m["mask.pie.sweepAngle"] = PropBinding.of(
            { el, v -> if (v != null) el.style().visual().maskType(MaskType.PIE).maskParam2(ValueCoercion.toFloat(v)) },
            { el -> el.style().visual().maskParam2().toDouble() },
        )
    }

    // ===== 注册助手 =====

    private fun MutableMap<String, PropBinding>.visualFloat(
        key: String,
        set: (StyleVisual, Float) -> Unit,
        get: (StyleVisual) -> Float,
    ) {
        this[key] = PropBinding.of(
            { el, v -> if (v != null) set(el.style().visual(), ValueCoercion.toFloat(v)) },
            { el -> get(el.style().visual()).toDouble() },
        )
    }

    private fun MutableMap<String, PropBinding>.visualColor(
        key: String,
        set: (StyleVisual, Int) -> Unit,
        get: (StyleVisual) -> Int,
    ) {
        this[key] = PropBinding.of(
            { el, v -> if (v != null) set(el.style().visual(), ValueCoercion.toIntColor(v)) },
            { el -> get(el.style().visual()) },
        )
    }

    private fun MutableMap<String, PropBinding>.visualBool(
        key: String,
        set: (StyleVisual, Boolean) -> Unit,
        get: (StyleVisual) -> Boolean,
    ) {
        this[key] = PropBinding.of(
            { el, v -> if (v != null) set(el.style().visual(), ValueCoercion.toBool(v)) },
            { el -> get(el.style().visual()) },
        )
    }

    /** Text 专属读写绑定：非 Text 元素打 unsupported（写）/ 返回 null（读）。 */
    private fun MutableMap<String, PropBinding>.textProp(
        key: String,
        write: (Text, Any?) -> Unit,
        read: (Text) -> Any?,
    ) {
        this[key] = PropBinding.of(
            { el, v -> if (el is Text) write(el, v) else PropDiag.unsupported(LOGGER, el, key) },
            { el -> if (el is Text) read(el) else null },
        )
    }

    /** 单角圆角读写绑定：写经 [applyCornerCorner] 保留其它三角，读取对应角当前值。 */
    private fun cornerCornerBinding(side: CornerSide, get: (StyleVisual) -> Float): PropBinding = PropBinding.of(
        { el, v -> if (v != null) applyCornerCorner(el, side, ValueCoercion.toFloat(v)) },
        { el -> get(el.style().visual()).toDouble() },
    )

    // ===== 字体合并 =====

    /** 以当前 font 为基底重建 [FontStyle]，由 [override] 覆写其中一个字段（null=继承上层三态）。 */
    private inline fun mergeFont(el: UIElement<*>, override: FontStyle.Builder.() -> Unit) {
        val current = el.font()
        el.font(
            FontStyle.builder()
                .name(current?.name())
                .shadow(current?.shadow())
                .bold(current?.bold())
                .italic(current?.italic())
                .apply(override)
                .build(),
        )
    }

    // ===== 圆角 =====

    /** 圆角四角枚举（`ButtonStatePropContributor` 复用 [applyCornerCorner] 时引用）。 */
    internal enum class CornerSide { TL, TR, BR, BL }

    /**
     * 把 [CornerRadii] 写到元素：Button / Slot / ProgressBar 等持有独立 cornerRadii 的元素走自身 setter，
     * 其它元素走 `style().visual().cornerRadii(...)`。`ButtonStatePropContributor` 复用此 helper 避免复制。
     */
    internal fun applyCornerRadii(el: UIElement<*>, radii: CornerRadii) {
        when (el) {
            is Button -> el.cornerRadii(radii)
            is Slot -> el.cornerRadii(radii)
            else -> el.style().visual().cornerRadii(radii)
        }
    }

    /** 写单一角圆角，其它角保留；由 [applyCornerRadii] 派生确保单角与全量语义一致（`ButtonStatePropContributor` 复用）。 */
    internal fun applyCornerCorner(el: UIElement<*>, side: CornerSide, v: Float) {
        val cur = readCornerRadii(el)
        val next = when (side) {
            CornerSide.TL -> cur.toBuilder().tl(v).build()
            CornerSide.TR -> cur.toBuilder().tr(v).build()
            CornerSide.BR -> cur.toBuilder().br(v).build()
            CornerSide.BL -> cur.toBuilder().bl(v).build()
        }
        applyCornerRadii(el, next)
    }

    /** 读当前 CornerRadii：Button / Slot 优先用自身字段，其它元素读 visual。 */
    private fun readCornerRadii(el: UIElement<*>): CornerRadii {
        if (el is Button) {
            el.cornerRadii()?.let { return it }
        }
        if (el is Slot) {
            el.cornerRadii()?.let { return it }
        }
        return el.style().visual().cornerRadii()
    }

    // ===== 渐变 / 遮罩解析 =====

    private fun setGradientType(el: UIElement<*>, v: Any) {
        val name = v.toString().trim().uppercase()
        try {
            el.style().visual().gradientType(GradientType.valueOf(name))
        } catch (e: IllegalArgumentException) {
            LOGGER.warn("非法 gradientType: $v", e)
        }
    }

    private fun setMaskType(el: UIElement<*>, v: Any) {
        val name = v.toString().trim().uppercase()
        try {
            el.style().visual().maskType(MaskType.valueOf(name))
        } catch (e: IllegalArgumentException) {
            LOGGER.warn("非法 mask 类型: $v", e)
        }
    }

    /** 解析渐变停止点 List：每项须为 [offset, argbColor] 二元列表，非法项跳过。 */
    private fun parseGradientStops(raw: List<*>): List<GradientStop> = raw.mapIndexedNotNull { i, item ->
        if (item !is List<*> || item.size < 2) {
            LOGGER.warn("gradientStops[$i] 期望 [offset, argbColor]，已跳过")
            null
        } else {
            GradientStop(ValueCoercion.toFloat(item[0]), ValueCoercion.toIntColor(item[1]))
        }
    }

    /**
     * 把简化两色写法合并到 visual.gradientStops：空则初始化两端默认 stop 后覆盖；
     * 仅 1 个 stop 时按写入端补齐另一端；≥2 时仅覆盖首/尾颜色，保留中间停点。
     */
    private fun applyGradientEdgeColor(el: UIElement<*>, argb: Int, atStart: Boolean) {
        val current = ArrayList(el.style().visual().gradientStops())
        if (current.isEmpty()) {
            current.add(GradientStop(0f, 0))
            current.add(GradientStop(1f, 0))
        } else if (current.size == 1) {
            val only = current[0]
            if (atStart) current.add(0, GradientStop(0f, only.argb)) else current.add(GradientStop(1f, only.argb))
        }
        val targetIdx = if (atStart) 0 else current.size - 1
        val old = current[targetIdx]
        current[targetIdx] = GradientStop(old.offset, argb)
        el.style().visual().gradientStops(current)
    }

    /** 把 DSL list-of-map 解析为 [ShadowSpec] 列表；非 Map 元素跳过并告警，缺省值见 [shadowFloat]。 */
    private fun parseBoxShadows(raw: List<*>): List<ShadowSpec> = raw.mapIndexedNotNull { i, item ->
        if (item !is Map<*, *>) {
            LOGGER.warn("boxShadows[$i] 期望 Map，已跳过")
            null
        } else {
            ShadowSpec(
                shadowFloat(item, "offsetX"),
                shadowFloat(item, "offsetY"),
                shadowFloat(item, "blur"),
                shadowFloat(item, "spread"),
                if (item.containsKey("color")) ValueCoercion.toIntColor(item["color"]) else 0,
                item.containsKey("inset") && ValueCoercion.toBool(item["inset"]),
            )
        }
    }

    /** 读阴影 map 中的浮点字段；缺失返回 0。 */
    private fun shadowFloat(map: Map<*, *>, key: String): Float = map[key]?.let { ValueCoercion.toFloat(it) } ?: 0f

    /** 校验 List 元素全为 Number 并返回；含非数字元素时告警并返回 null（中止整条 cornerRadius 写入）。 */
    private fun toNumberList(raw: List<*>): List<Number>? {
        val nums = ArrayList<Number>(raw.size)
        for (item in raw) {
            if (item !is Number) {
                LOGGER.warn("cornerRadius 数组元素期望数字，实际 ${item?.let { it::class.simpleName } ?: "null"}")
                return null
            }
            nums.add(item)
        }
        return nums
    }

    private fun textValueOf(el: UIElement<*>): String? = when (el) {
        is Text -> el.text()
        is Button -> el.text()
        else -> null
    }
}
